import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import InputField from "common/customInputField";
import SelectInput from "common/selectInput";
import MultiSelectInput from "common/multiSelectInput";
import Button from "react-bootstrap/Button";
import Modal from "common/modal";
import { pdf } from "@react-pdf/renderer";
import {
  getCategoriesList,
  getProductList,
  getLaborCodesList,
  clearLookupData,
  getStoreNumList,
  addProductCategory,
  getlaborCodeOptions,
  getCategoriesData,
  getLineItemsData,
  getProgramDetailsData,
  getLineItemPricesData,
  addLineItemPrices,
  updateLineItemPrices,
  editProgramDetail,
  updateLineItemData,
} from "store/programBuilder/actionCreator";
import pdfIcon from "svg/pdfIcon.svg";
import edit from "svg/edit_blue.svg";
import deleteIcon from "svg/delete_darkgray.svg";
import { truncateToTwoDecimal, formatPrice, checkForMetaKeys, removeFCcenter } from "utils/common";
import CustomTooltip from "common/tooltip";
import ProgramPdfDocument from "../programPdfDocument";
import ModalContents from "../modals/EditProgramDetailsModal";
import isEmpty from "lodash/isEmpty";
import max from "lodash/max";
import isNil from "lodash/isNil";
import partition from "lodash/partition";
import sortBy from "lodash/sortBy";
import DeleteLineModal from "../modals/DeleteLineModal";
import { api } from "store/slices/api";
import { toast } from "react-toastify";
import "./style.scss";

const sortPBItems = (items) => {
  const [alacarte, others] = partition(items, (item) => item.categoryId === 11);
  const products = sortBy(
    alacarte.flatMap((item) => item.products),
    ["name", "unitOfMeasurement"],
  );
  const laborCodes = sortBy(
    alacarte.flatMap((item) => item.laborCodes),
    (lc) => (isEmpty(lc?.name) ? `${lc?.laborCode ?? ""} - ${lc?.laborCodeDesc ?? ""}` : lc.name),
  );
  return [...others, { ...alacarte[0], productId: null, products, laborCodes }];
};

const ProgramDataLoader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { programId } = useParams();

  const { storeNumberList, laborCodeOptions, programDetailsData } = useSelector((state) => state.programBuilder);

  useEffect(() => {
    dispatch(getProgramDetailsData(programId));
  }, []);

  useEffect(() => {
    if (programDetailsData) {
      if (!programDetailsData.programId) {
        navigate("/programBuilder");
        toast.error("Invalid program ID.");
      }

      // TODO: some of these calls we can defer
      dispatch(getCategoriesData(programId));
      dispatch(getLineItemsData(programId));
      dispatch(getLineItemPricesData(programId));
      dispatch(getStoreNumList());
      dispatch(getlaborCodeOptions());
    }
  }, [programDetailsData]);

  if (!programDetailsData || isEmpty(storeNumberList) || isEmpty(laborCodeOptions)) {
    return <></>;
  }

  return <ProgramHomePage />;
};

const initialItems = {
  productId: "",
  name: "",
  laborCodeId: "",
  laborCode: "",
  laborCodeDescription: "",
  unitOfMeasurement: "",
};

const ProgramHomePage = () => {
  const dispatch = useDispatch();
  const { programId } = useParams();
  const tableBodyNode = useRef();
  const categoryRef = useRef();
  const storeNumRef = useRef();
  const laborCodesNode = useRef();
  const editInputRef = useRef();
  const editUnitRef = useRef();
  const dragMarket = useRef(0);
  const draggedOverMarket = useRef(0);
  const lineItemsRef = useRef();
  const marketInputRef = useRef();
  const thClass = `table-root-prog__header__tr__th table-root-prog__body__tr__td__text sec-divider`;
  const { sub } = useSelector((state) => state.loggedInUser);
  const {
    categoriesList,
    productsList,
    laborCodesList,
    storeNumberList,
    laborCodeOptions,
    categoriesData,
    lineItemsList,
    programDetailsData,
    lineItemPricesData,
  } = useSelector((state) => state.programBuilder);

  const { data: employee } = api.useGetEmployeeQuery(sub);
  const { data: typesList } = api.useGetPbTypesQuery();
  const { data: markets = [] } = api.useGetPbMarketsQuery(programId);

  const [updateMarket] = api.useUpdatePbMarketMutation();
  const [addMarket] = api.useAddPbMarketMutation();

  const initialLineItemsData = { productId: "", productName: "", padName: "", padDescripton: "" };
  const [dragAllowed, setDragAllowed] = useState(false);
  const [pricesCarryover, setPricesCarryover] = useState(programDetailsData?.productPricesCarryover ?? true);
  const [isShowAddCategoryModal, setIsShowAddCategoryModal] = useState({ isShow: false, typeId: "" });
  const [isShowAddMarketModal, setIsShowAddMarketModal] = useState(false);
  const [categoryData, setCategoryData] = useState({ categoryId: "", typeId: "", categoryName: "" });
  const [tableItemsColumnData, setTableItemsColumnData] = useState([]);
  const [tablePricesData, setTablePricesData] = useState({});
  const [totalPriceForProduct, setTotalPriceForProduct] = useState({});
  const [marketData, setMarketData] = useState({ storeNumber: "", marketName: "", rank: 0 });
  const [marketDataErrors, setMarketDataErrors] = useState({ storeNumber: "", marketName: "" });
  const [categoryError, setCategoryError] = useState("");
  const [unitErrorMsg, setUnitErrorMsg] = useState("");
  const [productNameError, setProductNameError] = useState("");
  const [isCategorySelected, setIsCategorySelected] = useState(false);
  const [selectedLaborCodes, setSelectedLaborCodes] = useState([]);
  const [existingLaborCode, setExistingLaborCode] = useState([]);
  const [isEditing, setIsEditing] = useState(new Map());
  const [alaCarteItems, setAlaCarteItems] = useState({
    products: [],
    laborCodes: [],
    isShowProductInput: false,
    isShowLaborInput: false,
  });
  const [alaCarteData, setAlaCarteData] = useState(initialItems);
  const [lineItems, setLineItems] = useState({ products: [], laborCodes: [] });
  const [lineItemsData, setLineItemsData] = useState(initialLineItemsData);
  const [showEditProgramModal, setShowEditProgramModal] = useState(false);
  const [showDeleteLineModal, setShowDeleteLineModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState({});
  const [isMarketDataChanged, setIsMarketDataChanged] = useState(false);
  const [isEditMarket, setIsEditMarket] = useState(false);
  const [doubleClicked, setDoubleClicked] = useState(false);

  const canEdit = sub == programDetailsData?.userId;
  const hasCoverLetter = programDetailsData?.isCoverLetterAdded ?? false;
  const orderedMarkets = programDetailsData.sortByMarket
    ? markets.toSorted((a, b) => a.name?.localeCompare(b.name))
    : markets.toSorted((a, b) => a.rank - b.rank);
  const highestRank = max(orderedMarkets.map((m) => m.rank)) ?? orderedMarkets.length;

  useEffect(() => {
    let tempItemsRowData = [];
    let laborCodesArr = [];
    categoriesData?.map((category) => {
      let tempObj = {};
      tempObj.categoryId = category.categoryId;
      tempObj.categoryName = category.categoryName;
      tempObj.typeId = category.typeId;
      tempObj.subTotal = "Subtotal";
      tempObj.products = [];
      tempObj.laborCodes = [];
      tempObj.productId = category.productId;
      lineItemsList?.map((lineItem) => {
        if (category.productId.toString() === lineItem.productId.toString()) {
          if (lineItem.laborCodeId) {
            tempObj.laborCodes.push(lineItem);
            if (category.typeId == 3) laborCodesArr.push(lineItem.laborCode);
          } else if (!lineItem.laborCodeId) {
            tempObj.products.push(lineItem);
          }
        }
      });
      if (tempObj.laborCodes?.length || tempObj.products?.length) tempItemsRowData.push(tempObj);
    });
    setExistingLaborCode(laborCodesArr);
    setTableItemsColumnData(tempItemsRowData);
  }, [categoriesData, lineItemsList]);

  useEffect(() => {
    const productIdMap = {};
    const filteredLineItemList = {};
    const totals = {};

    lineItemsList.forEach((item) => {
      productIdMap[item.lineItemId] = {
        name: item.name,
        productId: item.productId,
      };
    });

    lineItemPricesData?.forEach((obj) => {
      const productId = productIdMap[obj.lineItemId]?.productId;
      const name = productIdMap[obj.lineItemId]?.name;
      if (productId) {
        if (!filteredLineItemList[obj.marketId]) {
          filteredLineItemList[obj.marketId] = [];
        }
        filteredLineItemList[obj.marketId].push({
          ...obj,
          price: obj.price,
          productId: productId,
          name: name,
        });
      }
    });

    Object.keys(filteredLineItemList).forEach((marketId) => {
      filteredLineItemList[marketId].forEach((item) => {
        const { productId, price } = item;
        const isPadItem = item.name?.substring(0, 3) === "Pad";
        const parsedPrice = (isPadItem ? 0 : parseFloat(price)) || 0;

        totals[productId] = totals[productId] || {};
        totals[productId][marketId] = (totals[productId][marketId] || 0) + parsedPrice;
      });
    });

    Object.keys(totals).forEach((productId) => {
      Object.keys(totals[productId]).forEach((marketId) => {
        totals[productId][marketId] = parseFloat(totals[productId][marketId]).toFixed(2);
      });
    });

    setTotalPriceForProduct(totals);
    setTablePricesData(filteredLineItemList);
  }, [lineItemPricesData, lineItemsList, categoriesData]);

  useEffect(() => {
    let laborCodesArr = [];
    alaCarteItems.laborCodes.map((item) => {
      laborCodesArr.push(item.laborCode);
    });
    setSelectedLaborCodes(laborCodesArr);
    setIsCategorySelected(true);
  }, [alaCarteItems.laborCodes]);

  useEffect(() => {
    setIsCategorySelected(true);
  }, [alaCarteItems.products]);

  useEffect(() => {
    let tempProductsArr = [];
    tempProductsArr.push({ name: lineItemsData.productName });
    if (lineItemsData.padDescripton !== "") {
      tempProductsArr.push({ name: `Pad - ${lineItemsData.padDescripton}` });
    } else if (lineItemsData.padName !== "") {
      tempProductsArr.push({ name: "Pad" });
    }
    lineItemsRef.current = { ...lineItems, products: tempProductsArr };
    setLineItems({ ...lineItems, products: tempProductsArr });
  }, [lineItemsData]);

  useEffect(() => {
    setLineItems({ ...lineItems, laborCodes: laborCodesList });
  }, [laborCodesList]);

  useEffect(() => {
    if (editInputRef.current && isEditing) {
      if (document?.activeElement?.name !== "unit") {
        editInputRef.current.focus();
        editInputRef.current.select();
      }

      if (editInputRef.current.getBoundingClientRect().left < tableHalfWidth) {
        slider.scrollLeft =
          slider.scrollLeft - (tableHalfWidth + 100 - editInputRef.current.getBoundingClientRect().left);
      }
    }
  }, [isEditing]);

  let uniqueIndexSet = new Set();
  const slider = document.querySelector(".table-responsive");
  const tableElement = document.querySelector(".program-builder__table");
  const tableHalfWidth = tableElement?.clientWidth / 2;

  const handleDragLeave = () => {
    setDragAllowed(false);
  };

  const handleDragOverMarket = (event) => {
    event.preventDefault();
    setDragAllowed(true);
  };

  const handleMarketSort = (market) => {
    if (!programDetailsData?.sortByMarket && dragAllowed) {
      const dragIndex = dragMarket.current;
      const dropIndex = draggedOverMarket.current;
      if (dragIndex === dropIndex) return;
      orderedMarkets?.splice(dragIndex, 1);
      orderedMarkets?.splice(dropIndex, 0, market);
      orderedMarkets?.forEach((item, index) => updateMarket({ ...item, rank: index + 1 }));
    }
    setDragAllowed(false);
  };

  const handlePricesCarryoverToggle = (e) => {
    const { checked } = e.target;
    dispatch(editProgramDetail({ ...programDetailsData, productPricesCarryover: checked }));
    setPricesCarryover(checked);
  };

  const selectedInputStyle = (key) => {
    if (isEditing.get(key))
      return {
        borderBlock: "2px solid #0d6efd40",
        borderInline: "2.5px solid #0d6efd40",
        borderRadius: "5px",
        boxShadow: "#0d6efd40 0px 0px 2px 1px",
      };
  };

  const handleEditClick = (key) => {
    if (canEdit) {
      if (isEditing.get(key)) {
        setDoubleClicked(true);
      } else setDoubleClicked(false);
      const temp = new Map(isEditing);
      temp.set(key, true);
      setIsEditing(temp);
    }
  };

  const resetEditingState = () => {
    setIsEditing((isEditing) => {
      const tempMap = new Map(isEditing);
      isEditing.forEach((_, key) => tempMap.set(key, false));
      return tempMap;
    });
  };

  const handleBlur = (event, marketId, lineItemId, productId, isPad = false) => {
    const isFirstMarket = orderedMarkets[0].marketId === marketId;
    const isProduct = [...lineItemsList].find((obj) => obj.laborCodeId === null && obj.lineItemId == lineItemId);
    const shouldPricesCarryover = pricesCarryover && isFirstMarket && isProduct;

    let x = event.target.value || 0;
    let y = event.target.defaultValue || 0;
    let newPrice = parseFloat(truncateToTwoDecimal(x));
    let prevPrice = parseFloat(truncateToTwoDecimal(y));

    const batchUpdates = { add: [], update: [] };

    const collectUpdate = (updateData, isNew) => {
      if (isNew) {
        batchUpdates.add.push(updateData);
      } else {
        batchUpdates.update.push(updateData);
      }
    };

    if (event.target.value === "" && event.target.defaultValue !== "") {
      updateTablePrices(null, marketId, lineItemId, productId, collectUpdate);
      newPrice = null;
    } else if (newPrice !== prevPrice || (event.target.value === "0" && event.target.defaultValue === "")) {
      updateTablePrices(newPrice, marketId, lineItemId, productId, collectUpdate);
    }

    if (shouldPricesCarryover && newPrice !== prevPrice) {
      orderedMarkets.forEach((market) => {
        if (market.marketId !== marketId) {
          updateTablePrices(newPrice, market.marketId, lineItemId, productId, collectUpdate);
        }
      });
    }

    if (!isPad) {
      updateTotalProductPrice(newPrice, prevPrice, marketId, productId);
    }

    resetEditingState();
    dispatchGroupedUpdates(batchUpdates);
  };

  const dispatchGroupedUpdates = (batchUpdates) => {
    if (batchUpdates.add.length > 0) {
      dispatch(addLineItemPrices(batchUpdates.add));
    }
    if (batchUpdates.update.length > 0) {
      dispatch(updateLineItemPrices(batchUpdates.update));
    }
  };

  const updateTablePrices = (currPrice, marketId, lineItemId, productId, collectUpdate) => {
    const newPrice = currPrice === null || currPrice === "" ? null : truncateToTwoDecimal(currPrice || 0);
    let tempTablePriceData = { ...tablePricesData };
    let priceObj = {
      marketId,
      lineItemId,
      price: newPrice,
      createdBy: sub,
    };

    if (tempTablePriceData?.hasOwnProperty(marketId)) {
      let itemToUpdate = tempTablePriceData[marketId].find((item) => item.lineItemId == lineItemId);

      if (itemToUpdate) {
        itemToUpdate.price = newPrice;
        collectUpdate(
          {
            lineItemPriceId: itemToUpdate.lineItemPriceId,
            price: newPrice,
          },
          false,
        );
      } else {
        tempTablePriceData[marketId].push({ ...priceObj, productId });
        collectUpdate(priceObj, true);
      }
    } else {
      tempTablePriceData[marketId] = [{ ...priceObj, productId }];
      collectUpdate(priceObj, true);
    }

    setTablePricesData(tempTablePriceData);
  };

  const updateTotalProductPrice = (newPrice, prevPrice, marketId, productId) => {
    let totalPriceForProductClone = { ...totalPriceForProduct };
    if (!totalPriceForProductClone[productId]) {
      totalPriceForProductClone[productId] = {};
    }
    let tempTotal =
      parseFloat(totalPriceForProductClone[productId][marketId] || 0) -
      parseFloat(prevPrice) +
      parseFloat(newPrice || 0);
    if (tempTotal != 0) totalPriceForProductClone[productId][marketId] = tempTotal.toFixed(2);

    setTotalPriceForProduct(totalPriceForProductClone);
  };

  const confirmAddMarket = async () => {
    if (marketData.storeNumber && marketData.marketName) {
      setIsMarketDataChanged(false);
      setIsEditMarket(false);
      setIsShowAddMarketModal(false);

      let payload = {
        programId,
        name: marketData.marketName,
        storeNumber: marketData.storeNumber,
        createdBy: sub,
        rank: highestRank + 1,
      };
      if (isEditMarket && isMarketDataChanged) {
        payload.marketId = marketData.id;
        payload.rank = marketData.rank || 0;
      }
      const isProduct = [...lineItemsList]?.filter((obj) => obj.laborCodeId === null);
      const shouldPricesCarryover = pricesCarryover && isProduct?.length > 0;
      let products = [];
      if (shouldPricesCarryover) {
        isProduct.forEach((product) => {
          let firstMarketProductPrice = lineItemPricesData?.find(
            (obj) => obj.marketId == orderedMarkets[0]?.marketId && obj.lineItemId == product.lineItemId,
          );
          if (firstMarketProductPrice) products.push(firstMarketProductPrice);
        });
      }

      const isUpdate = isEditMarket && isMarketDataChanged;
      const res = await (isUpdate ? updateMarket(payload) : addMarket(payload));
      const marketId = isUpdate ? payload.marketId : res?.data?.marketId;
      if (products.length > 0 && !isNil(marketId)) {
        await dispatch(
          addLineItemPrices(
            products.map(({ lineItemId, price, createdBy }) => ({ marketId, lineItemId, price, createdBy })),
          ),
        );
      }
      dispatch(getLineItemPricesData(programId));
    } else {
      let tempError = { storeNumber: "", marketName: "" };

      if (!marketData.storeNumber) tempError.storeNumber = "Please select a Store";
      if (!marketData.marketName) tempError.marketName = "Please enter Market Name";

      setMarketDataErrors(tempError);
    }
  };

  const handleAddCategory = (typeId) => {
    setCategoryError("");
    setProductNameError("");
    setIsShowAddCategoryModal({ isShow: true, typeId: typeId });
    dispatch(clearLookupData());
    setCategoryData({ categoryId: "", typeId: "", categoryName: "" });
    setLineItemsData(initialLineItemsData);
    setIsCategorySelected(false);
    if (typeId !== 3) {
      dispatch(getCategoriesList(typeId));
    }
  };

  const isShowAlaCarteInputs = (type, isShow) => {
    setAlaCarteData(initialItems);
    setProductNameError("");
    setUnitErrorMsg("");

    if (type === "product") {
      setAlaCarteItems({ ...alaCarteItems, isShowProductInput: isShow, isShowLaborInput: !isShow });
    } else if (type === "labor") {
      setAlaCarteItems({ ...alaCarteItems, isShowLaborInput: isShow, isShowProductInput: !isShow });
    }
    if (!isShow) {
      setAlaCarteItems({ ...alaCarteItems, isShowLaborInput: isShow, isShowProductInput: isShow });
    }
  };

  const addAlaCarteItems = (type) => {
    if (type === "product") {
      if (alaCarteData.name != "" && alaCarteData.unitOfMeasurement != "") {
        setProductNameError("");
        setUnitErrorMsg("");
        let tempProductsData = JSON.parse(JSON.stringify(alaCarteItems.products));
        tempProductsData.push(alaCarteData);
        setAlaCarteItems({ ...alaCarteItems, products: tempProductsData, isShowProductInput: false });
      } else {
        if (alaCarteData.name) setProductNameError("");
        else setProductNameError("Please enter product name");
      }
    } else {
      if (alaCarteData.laborCodeId && alaCarteData.unitOfMeasurement) {
        setProductNameError("");
        setUnitErrorMsg("");
        let tempLaborData = JSON.parse(JSON.stringify(alaCarteItems.laborCodes));
        tempLaborData.push(alaCarteData);
        setAlaCarteItems({ ...alaCarteItems, laborCodes: tempLaborData, isShowLaborInput: false });
      } else {
        if (alaCarteData.laborCodeId) setProductNameError("");
        else setProductNameError("Please select a labor code");
      }
    }

    if (alaCarteData.unitOfMeasurement) setUnitErrorMsg("");
    else setUnitErrorMsg("Please enter unit of measurement");
  };

  const onAlaCarteFieldsChange = (e) => {
    const { id, value } = e.target;
    if (id === "productName") {
      setAlaCarteData({
        ...alaCarteData,
        productId: "",
        name: value,
        laborCodeId: "",
        laborCode: "",
        laborCodeDescription: "",
      });
    }
    if (id === "unitOfMeasurement") {
      if (value.length > 20) setUnitErrorMsg("Cannot exceed 20 characters");
      else {
        setUnitErrorMsg("");
        setAlaCarteData({ ...alaCarteData, unitOfMeasurement: value });
      }
    }
  };

  const handleLaborCodeLookupValue = (e, value, prd, clickevn) => {
    const laborDetails = JSON.parse(clickevn.target.dataset.selectedmetadata);
    setAlaCarteData({
      ...alaCarteData,
      productId: "",
      name: "",
      laborCodeId: laborDetails.id,
      laborCode: laborDetails.laborCode,
      laborCodeDescription: laborDetails.description,
    });
  };

  const confirmAddCategory = (typeId) => {
    if (typeId == 3) {
      setIsShowAddCategoryModal({ ...isShowAddCategoryModal, isShow: false });
      dispatch(
        addProductCategory({
          ...categoryData,
          ...alaCarteItems,
          programId: programId,
          createdBy: sub,
          isAlaCarte: true,
        }),
      );
      setAlaCarteItems({ products: [], laborCodes: [], isShowProductInput: false, isShowLaborInput: false });
    } else {
      if (categoryData.categoryId && lineItemsData.productName) {
        setIsShowAddCategoryModal({ ...isShowAddCategoryModal, isShow: false });
        if (typeId !== 3) {
          dispatch(
            addProductCategory({
              ...categoryData,
              ...lineItems,
              ...lineItemsRef.current,
              programId: programId,
              createdBy: sub,
              isAlaCarte: false,
            }),
          );
        }
      } else {
        if (!categoryData.categoryId) setCategoryError("Please select a Category");
        if (!lineItemsData.productName) setProductNameError("Please enter Product name");
      }
    }
  };

  const handleCancelModal = () => {
    setAlaCarteItems({ products: [], laborCodes: [], isShowProductInput: false, isShowLaborInput: false });
    setIsShowAddCategoryModal({ ...isShowAddCategoryModal, isShow: false });
    setIsEditMarket(false);
    setIsMarketDataChanged(false);
    setMarketDataErrors({ storeNumber: "", marketName: "" });
    setIsShowAddMarketModal(false);
  };

  const handleDeleteMarket = () => {
    if (marketData.storeNumber && marketData.marketName) {
      setItemToDelete({ ...marketData, deleteTarget: "market" });
      setShowDeleteLineModal(true);
      handleCancelModal();
    }
  };

  const onCategoryChange = (e, val) => {
    setIsCategorySelected(true);
    if (lineItemsData.productName) setProductNameError("");

    if (val.categoryId == "") {
      setCategoryError("Please select a Category");
    }

    setCategoryData({ ...categoryData, categoryId: val.categoryId, typeId: val.typeId, categoryName: val.name });
    if (val.categoryId) {
      setCategoryError("");
      if (val.categoryId == 1) {
        setLineItemsData({ ...lineItemsData, padName: "Pad" });
      } else {
        setLineItemsData({ ...lineItemsData, padName: "" });
      }
      dispatch(getProductList(val.categoryId));
      dispatch(getLaborCodesList(val.categoryId));
    }
  };

  const onLineItemsChange = (e) => {
    const { id, value } = e.target;
    if (id === "productName") {
      if (!value) setProductNameError("Please enter Product name");
      else setProductNameError("");
      setLineItemsData({ ...lineItemsData, productName: value });
    } else if (id === "padDescripton") {
      setLineItemsData({ ...lineItemsData, padDescripton: value });
    }
  };

  const handleStoreValues = (e, value, prd, clickevn) => {
    const storeDetails = JSON.parse(clickevn.target.dataset.selectedmetadata);
    setMarketData({
      ...marketData,
      storeNumber: storeDetails.id,
      marketName: removeFCcenter(storeDetails.description),
    });
    setMarketDataErrors({ storeNumber: "", marketName: "" });
    marketInputRef.current.value = removeFCcenter(storeDetails.description);
    setIsMarketDataChanged(true);
  };

  const scroll = (dir) => {
    if (dir == "left") slider.scrollLeft = slider.scrollLeft - 520;
    else if (dir == "right") slider.scrollLeft = slider.scrollLeft + 520;
    else if (dir == "left-slow") slider.scrollLeft = slider.scrollLeft - 280;
    else if (dir == "left-div") slider.scrollLeft = slider.scrollLeft - 100;
  };

  const validateLength = (event) => {
    if (checkForMetaKeys(event.key)) {
      return;
    } else if (event.target.value.length >= 100) {
      toast.error("Cannot exceed 100 characters");
    }
  };

  const handleUnitEdit = (categoryItem, lineItem, event, target = "lineItem") => {
    const { value } = event.target;
    const tempColumnData = JSON.parse(JSON.stringify(tableItemsColumnData));
    const editedProduct = tempColumnData.find((item) => item.productId == categoryItem.productId);
    if (value.length > 20) {
      toast.error("Cannot exceed 20 characters");
    } else if (editedProduct && value) {
      let itemToUpdate = editedProduct[target].find((item) => item.lineItemId == lineItem.lineItemId);
      if (itemToUpdate) {
        itemToUpdate.unitOfMeasurement = value;
        setTableItemsColumnData(tempColumnData);
      }
    }
  };

  const handleUnitBlur = (lineItem, event) => {
    let payload = {
      lineItemId: lineItem.lineItemId,
      name: lineItem.name,
      unitOfMeasurement: lineItem.unitOfMeasurement,
      createdBy: sub,
    };

    if (event.relatedTarget?.name != "alacarte") {
      dispatch(updateLineItemData(payload));
      resetEditingState();
    } else {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  };

  const checkForPad = (event) => {
    if (
      event.target.defaultValue.toLowerCase().slice(0, 3) != "pad" &&
      event.target.value.toLowerCase().slice(0, 3) == "pad"
    ) {
      toast.error("Cannot use 'Pad' as product name. Please try a different name.");
    }
  };

  const handleLineItemEdit = (categoryItem, lineItem, event, deleteTarget = "lineItem") => {
    const { relatedTarget, target } = event;
    const isDelete = relatedTarget?.name === "delete-icon";
    const hasChangedValue = target.value !== target.defaultValue;

    if (isDelete) {
      handleDeleteLineItem(event, deleteTarget, lineItem.lineItemId);
      return;
    }

    if (deleteTarget === "products" && target.value === "" && categoryItem.typeId !== 3) {
      resetEditingState();
      return;
    }

    const isPad = target.defaultValue?.startsWith("Pad");
    let value;

    if (isPad) {
      value = target.value.toLowerCase().startsWith("pad")
        ? target.value.slice(0, 100)
        : `Pad - ${target.value.slice(0, 94)}`;
    } else {
      value = target.value.toLowerCase().startsWith("pad") ? "" : target.value.slice(0, 100);
    }

    const tempColumnData = JSON.parse(JSON.stringify(tableItemsColumnData));
    const editedProduct = tempColumnData.find((item) => item.productId === categoryItem.productId);

    if (editedProduct && value) {
      const itemToUpdate = editedProduct[deleteTarget].find((item) => item.lineItemId === lineItem.lineItemId);

      if (itemToUpdate) {
        itemToUpdate.name = value;
        setTableItemsColumnData(tempColumnData);

        const payload = {
          lineItemId: lineItem.lineItemId,
          name: value,
          unitOfMeasurement: categoryItem.typeId === 3 ? itemToUpdate.unitOfMeasurement : null,
          createdBy: sub,
        };

        if (relatedTarget?.name !== "unit") {
          resetEditingState();
          if (hasChangedValue) dispatch(updateLineItemData(payload));
        } else {
          editUnitRef.current.focus();
          editUnitRef.current.select();
        }
      }
      return;
    }

    resetEditingState();
  };

  const handleDeleteLineItem = (event, deleteTarget, lineItemId) => {
    setItemToDelete({ ...event, deleteTarget, lineItemId });
    setShowDeleteLineModal(true);
    resetEditingState();
  };

  const deleteConfirmationModal = () => {
    const { deleteTarget } = itemToDelete;
    if (deleteTarget === "market") {
      return (
        <div>
          {" "}
          Are you sure you want to delete {itemToDelete?.storeNumber} - {itemToDelete?.marketName}?
        </div>
      );
    } else if (deleteTarget == "category")
      return (
        <div>
          {" "}
          Are you sure you want to delete {itemToDelete?.categoryName}? Doing so will delete all line items under it as
          well.
        </div>
      );
    else {
      const { target } = itemToDelete;
      return <div> Are you sure you want to delete {target?.value}?</div>;
    }
  };

  const cursorType = () => {
    if (programDetailsData.sortByMarket) return { cursor: "default" };
    return { cursor: "move" };
  };

  const handleNavigation = (
    currentMarketIndex,
    currentUniqueIndex,
    event,
    marketId,
    lineItemId,
    productId,
    isPad = false,
  ) => {
    const navigationKeys = new Set(["Tab", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]);

    if (!navigationKeys.has(event.code)) return;

    const direction = {
      Tab: { horizontal: 1, vertical: 0 },
      ArrowRight: { horizontal: 1, vertical: 0 },
      ArrowLeft: { horizontal: -1, vertical: 0 },
      Enter: { horizontal: 0, vertical: 1 },
      ArrowDown: { horizontal: 0, vertical: 1 },
      ArrowUp: { horizontal: 0, vertical: -1 },
    }[event.code] || { horizontal: 0, vertical: 0 };

    const { horizontal, vertical } = direction;
    if (horizontal === 0 && vertical === 0) return;

    if (!doubleClicked || event.code === "Tab" || event.code === "Enter") {
      event.preventDefault();

      const uniqueIndexStr = currentUniqueIndex.toString();
      const marketIdStr = marketId.toString();
      const uniqueIndexSuffix = uniqueIndexStr.slice(marketIdStr.length);
      const filteredKeys = [...uniqueIndexSet].filter((key) => key.startsWith(marketId));
      const currentIndex = filteredKeys.indexOf(currentUniqueIndex);
      let nextKey;

      if (horizontal) {
        const nextMarketIndex = currentMarketIndex + horizontal;
        nextKey = orderedMarkets[nextMarketIndex]
          ? `${orderedMarkets[nextMarketIndex].marketId}${uniqueIndexSuffix}`
          : null;
      } else if (vertical) {
        const nextIndex = currentIndex + vertical;
        nextKey = filteredKeys[nextIndex] || null;
      }

      if (nextKey) {
        handleBlur(event, marketId, lineItemId, productId, isPad);

        const updatedEditingState = new Map(isEditing);
        updatedEditingState.set(currentUniqueIndex, false);
        updatedEditingState.set(nextKey, true);

        setIsEditing(updatedEditingState);

        if (horizontal === -1) {
          scroll("left-div");
        }
      }
    }
  };

  const sortedTableItemsColumnData = sortPBItems(tableItemsColumnData);

  return (
    <>
      <div className="container-fluid search-form-container">
        <div className="search-form-container__title d-flex justify-content-between align-items-center flex-column flex-md-row align-items-md-center">
          <span className="search-form-container__title--text mb-4 mb-md-0">
            Program ID: {programDetailsData?.programId}
          </span>
          <div className="d-flex">
            {canEdit && (
              <Button className="me-0" variant="outline-primary" onClick={() => setShowEditProgramModal(true)}>
                <img src={edit} alt="edit-program" />
                &nbsp; Edit
              </Button>
            )}
            {orderedMarkets?.length > 0 && sortedTableItemsColumnData?.length > 0 && (
              <Button
                className="me-0"
                variant="outline-primary"
                onClick={async () => {
                  const blob = await pdf(
                    <ProgramPdfDocument
                      hasCoverLetter={hasCoverLetter}
                      userData={employee}
                      programDetailsData={programDetailsData}
                      orderedMarkets={orderedMarkets}
                      tableItemsColumnData={sortedTableItemsColumnData}
                      tablePricesData={tablePricesData}
                      totalPriceForProduct={totalPriceForProduct}
                    />,
                  ).toBlob();

                  const pdfURL = URL.createObjectURL(blob);
                  window.open(pdfURL, "_blank");
                }}
              >
                <img src={pdfIcon} alt="download-program" className="me-1" />
                Create PDF
              </Button>
            )}
          </div>
        </div>

        <div className="d-flex flex-column flex-md-row">
          <div className="flex-grow-1">
            <div className="d-md-flex flex-column flex-md-row mt-5 mt-md-0 overflow-auto position-relative">
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
                <div className="info-item__label">Customer Name</div>
                <div className="info-item__value text-end text-md-start">{programDetailsData?.customerName}</div>
              </div>
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-4">
                <div className="info-item__label">Description</div>
                <div className="info-item__value text-end text-md-start" style={{ overflowWrap: "anywhere" }}>
                  {programDetailsData?.description}
                </div>
              </div>
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-5">
                <div className="info-item__label">Parent</div>
                <div className="info-item__value text-end text-md-start">
                  {programDetailsData?.parentNumber || programDetailsData?.parentName ? (
                    <>
                      {programDetailsData?.parentNumber}
                      {programDetailsData?.parentNumber && programDetailsData?.parentName && " - "}
                      {programDetailsData?.parentName}
                    </>
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
            </div>
            <div className="d-md-flex flex-column flex-md-row mt-5 mt-md-4 overflow-lg-auto position-relative prog-date-time-info justify-content-start">
              <div className="date-sty">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="label">Created By: </div>
                  <div className="value">{programDetailsData?.userId}</div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="label">Created Date: </div>
                  <div className="value">
                    {programDetailsData?.createdDate
                      ? new Date(programDetailsData?.createdDate).toLocaleDateString("en-US")
                      : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-row flex-md-column info-item justify-content-center col-12 col-md-3">
            <div className="info-item__value text-end">
              {programDetailsData?.customerLogo ? (
                <img
                  src={programDetailsData?.customerLogo}
                  alt="logo"
                  style={{ maxHeight: "120px", maxWidth: "100%" }}
                  className="object-fit-fill rounded"
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
      {canEdit && (
        <>
          <div className="pdfOptions__options d-flex justify-content-between">
            <div className="d-flex align-items-center justify-content-end">
              <span className="pdfOptions pdf-toggle my-0 mx-3">
                <label className="switch">
                  <input
                    type="checkbox"
                    id="pricesCarryover"
                    onChange={(e) => handlePricesCarryoverToggle(e)}
                    checked={programDetailsData?.productPricesCarryover ?? true}
                  ></input>
                  <span className="slider round"></span>
                </label>
              </span>
              <span className="pdfOptions__options--text">Product prices carryover</span>
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <span className="pdfOptions__options--text">Alphabetical order</span>
              <span className="pdfOptions pdf-toggle my-0 mx-3">
                <label className="switch">
                  <input
                    type="checkbox"
                    id="order"
                    onChange={(e) =>
                      dispatch(editProgramDetail({ ...programDetailsData, sortByMarket: !e.target.checked }))
                    }
                    checked={!programDetailsData?.sortByMarket}
                  ></input>
                  <span className="slider round"></span>
                </label>
              </span>
              <span className="pdfOptions__options--text">Customize market list order</span>
            </div>
          </div>
        </>
      )}
      <div className="search-table-wrapper">
        <div className="container-fluid prog-search-table">
          <div className="table-responsive program-builder__table">
            <table id="progBuilder" className="table table-root-prog">
              <thead className="table-root-floor__header">
                <tr className="table-root-floor__header__tr main-table-header overflow-visible">
                  <th
                    rowSpan="3"
                    className={`table-root-prog__header__tr__th__header-text table-root-prog__body__tr__td__text table-root-prog__header__tr__th__fixed-col items`}
                  >
                    <div className="items-block" onDragOver={() => scroll("left-slow")}>
                      <span className="flex-grow-1">Items</span>
                      <span className="section-divider"></span>
                    </div>
                  </th>
                  <th className="d-flex justify-content-center align-items-center">
                    <span className="col-6 d-flex align-items-center justify-content-end">Markets</span>
                    <span className="col-6 d-flex align-items-center justify-content-end">
                      {canEdit && (
                        <div
                          className="mx-2 add-item-btn"
                          onClick={() => {
                            setMarketData({ storeNumber: "", marketName: "", rank: 0 });
                            setMarketDataErrors({ storeNumber: "", marketName: "" });
                            setIsShowAddMarketModal(true);
                          }}
                        >
                          +Add Market
                        </div>
                      )}
                      <div onClick={() => scroll("left")} className="chevron">
                        &lt;
                      </div>
                      <div onClick={() => scroll("right")} className="chevron">
                        &gt;
                      </div>
                    </span>
                  </th>
                </tr>

                <tr
                  className="table-root-floor__header__tr table-header-display"
                  onDragOver={(e) => handleDragOverMarket(e)}
                  onDragEnter={() => {
                    draggedOverMarket.current = orderedMarkets?.length - 1;
                  }}
                >
                  {orderedMarkets.map((col, index) => (
                    <th
                      key={col.marketId}
                      className={`${thClass} marketCols`}
                      draggable={!programDetailsData?.sortByMarket}
                      onDragStart={() => {
                        dragMarket.current = index;
                      }}
                      onDragEnter={(e) => {
                        e.stopPropagation();
                        draggedOverMarket.current = index;
                      }}
                      onDragEnd={() => handleMarketSort(col)}
                      onDragOver={(e) => handleDragOverMarket(e)}
                      onDragLeave={() => handleDragLeave()}
                      style={cursorType()}
                    >
                      <CustomTooltip placement="top" trigger="hover" text={col.storeNumber + " - " + col.name}>
                        <div className="px-3">
                          {col.storeNumber}
                          <span
                            className="edit-market"
                            onClick={() => {
                              const { storeNumber, name, rank, marketId } = col;
                              setMarketData({ storeNumber, marketName: name, rank, id: marketId });
                              setIsEditMarket(true);
                              setIsShowAddMarketModal(true);
                            }}
                          >
                            <img src={edit} alt="edit" />
                          </span>
                        </div>
                        <div className="px-3">{col.name}</div>
                      </CustomTooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="table-root-prog__body" ref={tableBodyNode}>
                {typesList?.map((typeItem, index) => {
                  let globalTRIndex = 0;
                  return (
                    <React.Fragment key={typeItem.typeId}>
                      <tr
                        className="table-root-prog__body__tr table-root-prog__body__tr_category-header bg-grey"
                        style={{ height: 48 }}
                      >
                        <td
                          colSpan={orderedMarkets.length + 1}
                          className={`${thClass} overflow-visible`}
                          key={index + typeItem.typeId}
                        >
                          <span className="type-header sticky-type-header">{typeItem.name}</span>
                          {canEdit && (
                            <span
                              className="add-item-btn sticky-add-item-btn"
                              onClick={() => handleAddCategory(typeItem.typeId)}
                            >
                              +Add Item
                            </span>
                          )}
                        </td>
                      </tr>
                      {(sortedTableItemsColumnData ?? [])
                        .filter((c) => c.typeId === typeItem.typeId)
                        .map((categoryItem, categoryIndex) => (
                          <tr
                            id={`${index}${++globalTRIndex}`}
                            key={categoryIndex + categoryItem.categoryId}
                            className="table-root-prog__body__tr table-root-prog__body__tr__td__fixed-row product-row"
                          >
                            <td className={`${thClass} table-root-prog__header__tr__th__fixed-col`} key={categoryIndex}>
                              {categoryItem?.typeId != 3 && (
                                <div className="category-item">
                                  {categoryItem.categoryName}
                                  {canEdit && (
                                    <span>
                                      <img
                                        src={deleteIcon}
                                        className="actions-align"
                                        alt={"delete-icon"}
                                        onClick={() => handleDeleteLineItem(categoryItem, "category")}
                                      />
                                    </span>
                                  )}
                                </div>
                              )}

                              {(categoryItem?.products ?? [])
                                .filter((p) => p.name.substring(0, 3) !== "Pad")
                                .map((productItem) => (
                                  <div
                                    key={productItem.lineItemId}
                                    className="product-item product-item__row-align d-flex"
                                    style={selectedInputStyle(
                                      "product-" + categoryItem.productId + "-" + productItem.lineItemId,
                                    )}
                                    onClick={() =>
                                      handleEditClick(
                                        `${"product-" + categoryItem.productId + "-" + productItem.lineItemId}`,
                                      )
                                    }
                                  >
                                    {isEditing.get(
                                      "product-" + categoryItem.productId + "-" + productItem.lineItemId,
                                    ) ? (
                                      <>
                                        <input
                                          type="text"
                                          name={categoryItem.typeId == 3 ? "alacarte" : "lineItem"}
                                          ref={editInputRef}
                                          defaultValue={productItem.name || ""}
                                          className="product-item__category-input"
                                          onBlur={(event) =>
                                            handleLineItemEdit(categoryItem, productItem, event, "products")
                                          }
                                          onKeyDown={(event) => validateLength(event)}
                                          onChange={(event) => checkForPad(event)}
                                          maxLength={100}
                                        />
                                        {categoryItem.typeId == 3 && (
                                          <input
                                            type="text"
                                            ref={editUnitRef}
                                            name="unit"
                                            defaultValue={productItem.unitOfMeasurement}
                                            style={{
                                              left: "unset",
                                              right: "30px",
                                              borderLeft: "1px solid #d1d1d1",
                                              paddingLeft: "20px",
                                            }}
                                            className="product-item__category-input w-25"
                                            onChange={(event) =>
                                              handleUnitEdit(categoryItem, productItem, event, "products")
                                            }
                                            onBlur={(event) => handleUnitBlur(productItem, event)}
                                          />
                                        )}
                                        <span>
                                          <img
                                            tabIndex="-1"
                                            name="delete-icon"
                                            src={deleteIcon}
                                            className="actions-align"
                                            alt={"delete-icon"}
                                            style={{ position: "relative", left: "365px" }}
                                            onClick={(event) =>
                                              handleDeleteLineItem(event, "products", productItem.lineItemId)
                                            }
                                          />
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span>{productItem.name}</span>
                                        <span>
                                          {productItem.unitOfMeasurement && `(${productItem.unitOfMeasurement})`}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                ))}
                              {(categoryItem?.laborCodes ?? []).map((laborCodeItem) => (
                                <div
                                  key={laborCodeItem.lineItemId}
                                  className="product-item product-item__row-align d-flex"
                                  style={selectedInputStyle(
                                    "product-" + categoryItem.productId + "-" + laborCodeItem.lineItemId,
                                  )}
                                  onClick={() =>
                                    handleEditClick(
                                      `${"product-" + categoryItem.productId + "-" + laborCodeItem.lineItemId}`,
                                    )
                                  }
                                >
                                  {isEditing.get(
                                    "product-" + categoryItem.productId + "-" + laborCodeItem.lineItemId,
                                  ) ? (
                                    <>
                                      <input
                                        type="text"
                                        name={categoryItem.typeId == 3 ? "alacarte" : "lineItem"}
                                        ref={editInputRef}
                                        defaultValue={
                                          laborCodeItem.name ||
                                          `${laborCodeItem.laborCode} - ${laborCodeItem.laborCodeDesc}` ||
                                          ""
                                        }
                                        className="product-item__category-input"
                                        onBlur={(event) =>
                                          handleLineItemEdit(categoryItem, laborCodeItem, event, "laborCodes")
                                        }
                                        onKeyDown={(event) => validateLength(event)}
                                        maxLength={100}
                                      />
                                      {categoryItem.typeId == 3 && (
                                        <>
                                          <input
                                            type="text"
                                            ref={editUnitRef}
                                            name="unit"
                                            defaultValue={laborCodeItem.unitOfMeasurement}
                                            style={{
                                              left: "unset",
                                              right: "30px",
                                              borderLeft: "1px solid #d1d1d1",
                                              paddingLeft: "20px",
                                            }}
                                            className="product-item__category-input w-25"
                                            onChange={(event) =>
                                              handleUnitEdit(categoryItem, laborCodeItem, event, "laborCodes")
                                            }
                                            onBlur={(event) => handleUnitBlur(laborCodeItem, event)}
                                          />
                                        </>
                                      )}
                                      <span>
                                        <img
                                          tabIndex="-1"
                                          name="delete-icon"
                                          src={deleteIcon}
                                          className="actions-align"
                                          alt={"delete-icon"}
                                          style={{ position: "relative", left: "365px" }}
                                          onClick={(event) =>
                                            handleDeleteLineItem(event, "laborCodes", laborCodeItem.lineItemId)
                                          }
                                        />
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <span>
                                        {laborCodeItem.name ||
                                          `${laborCodeItem.laborCode} - ${laborCodeItem.laborCodeDesc}`}
                                      </span>
                                      <span>
                                        {laborCodeItem.unitOfMeasurement && ` (${laborCodeItem.unitOfMeasurement})`}
                                      </span>
                                    </>
                                  )}
                                </div>
                              ))}

                              {categoryItem?.typeId == "1" && categoryItem?.categoryId == "1" ? (
                                <>
                                  <div className="product-item product-item__row-align totalVal">
                                    Subtotal without Pad
                                  </div>
                                  {(categoryItem?.products ?? [])
                                    .filter((p) => p.name.substring(0, 3) === "Pad")
                                    .map((productItem) => (
                                      <React.Fragment key={productItem.lineItemId}>
                                        <div
                                          className="product-item product-item__row-align"
                                          style={selectedInputStyle(
                                            `product-${categoryItem.productId}-${productItem.lineItemId}`,
                                          )}
                                          onClick={() =>
                                            handleEditClick(
                                              `${"product-" + categoryItem.productId + "-" + productItem.lineItemId}`,
                                            )
                                          }
                                        >
                                          {isEditing.get(
                                            `product-${categoryItem.productId}-${productItem.lineItemId}`,
                                          ) ? (
                                            <>
                                              <input
                                                type="text"
                                                ref={editInputRef}
                                                defaultValue={productItem.name || ""}
                                                className="product-item__category-input"
                                                onBlur={(event) =>
                                                  handleLineItemEdit(categoryItem, productItem, event, "products")
                                                }
                                                onKeyDown={(event) => validateLength(event)}
                                                maxLength={100}
                                              />
                                              <span>
                                                <img
                                                  tabIndex="-1"
                                                  name="delete-icon"
                                                  src={deleteIcon}
                                                  className="actions-align"
                                                  alt={"delete-icon"}
                                                  style={{ position: "relative", left: "365px" }}
                                                />
                                              </span>
                                            </>
                                          ) : (
                                            <span>{productItem.name}</span>
                                          )}
                                        </div>
                                        <div className="product-item product-item__row-align totalVal">
                                          Subtotal with Pad
                                        </div>
                                      </React.Fragment>
                                    ))}
                                </>
                              ) : categoryItem?.typeId != 3 ? (
                                <div className="product-item product-item__row-align totalVal">
                                  {categoryItem.subTotal}
                                </div>
                              ) : null}
                            </td>

                            {tablePricesData
                              ? (orderedMarkets ?? []).map((market, marketIndex) => {
                                  let itemCounter = 0;
                                  return (
                                    <td className={`${thClass}`} key={`${categoryIndex}_${market.marketId}`}>
                                      {categoryItem?.typeId != 3 && <div className="category-item"></div>}
                                      {(categoryItem?.products ?? []).map((productItem, productItemIndex) => {
                                        if (
                                          productItem.name?.substring(0, 3) !== "Pad" &&
                                          (tablePricesData[market.marketId]?.length == 0 ||
                                            !tablePricesData[market.marketId]?.find(
                                              (x) =>
                                                x.productId == productItem.productId &&
                                                x.lineItemId == productItem.lineItemId,
                                            ))
                                        ) {
                                          const globalTDIndex = itemCounter++;
                                          const uniqueDivIndex = `${market.marketId}${index}${globalTRIndex}${globalTDIndex}`;
                                          uniqueIndexSet.add(uniqueDivIndex);
                                          return (
                                            <div
                                              id={uniqueDivIndex}
                                              key={uniqueDivIndex}
                                              className="product-item product-item__prices-align"
                                              style={selectedInputStyle(uniqueDivIndex)}
                                              onClick={() => handleEditClick(uniqueDivIndex)}
                                            >
                                              {isEditing.get(uniqueDivIndex) ? (
                                                <input
                                                  type="text"
                                                  ref={editInputRef}
                                                  className="product-item__prices-input"
                                                  onKeyDown={(event) =>
                                                    handleNavigation(
                                                      marketIndex,
                                                      uniqueDivIndex,
                                                      event,
                                                      market.marketId,
                                                      productItem.lineItemId,
                                                      categoryItem.productId,
                                                    )
                                                  }
                                                  onBlur={(event) =>
                                                    handleBlur(
                                                      event,
                                                      market.marketId,
                                                      productItem.lineItemId,
                                                      categoryItem.productId,
                                                    )
                                                  }
                                                />
                                              ) : (
                                                <span></span>
                                              )}
                                            </div>
                                          );
                                        }

                                        return (
                                          productItem.name?.substring(0, 3) !== "Pad" &&
                                          tablePricesData[market.marketId]?.length !== 0 &&
                                          tablePricesData[market.marketId]?.map((secKey, idx) => {
                                            if (
                                              secKey.productId == productItem.productId &&
                                              secKey.lineItemId == productItem.lineItemId
                                            ) {
                                              const globalTDIndex = itemCounter++;
                                              const uniqueDivIndex = `${market.marketId}${index}${globalTRIndex}${globalTDIndex}`;
                                              uniqueIndexSet.add(uniqueDivIndex);
                                              return (
                                                <div
                                                  id={uniqueDivIndex}
                                                  key={uniqueDivIndex}
                                                  className="product-item product-item__prices-align"
                                                  style={selectedInputStyle(uniqueDivIndex)}
                                                  onClick={() => handleEditClick(uniqueDivIndex)}
                                                >
                                                  {isEditing.get(uniqueDivIndex) ? (
                                                    <input
                                                      type="text"
                                                      ref={editInputRef}
                                                      defaultValue={
                                                        secKey.price === null || secKey.price === ""
                                                          ? ""
                                                          : truncateToTwoDecimal(secKey.price)
                                                      }
                                                      className="product-item__prices-input"
                                                      onKeyDown={(event) =>
                                                        handleNavigation(
                                                          marketIndex,
                                                          uniqueDivIndex,
                                                          event,
                                                          market.marketId,
                                                          productItem.lineItemId,
                                                          categoryItem.productId,
                                                        )
                                                      }
                                                      onBlur={(event) =>
                                                        handleBlur(
                                                          event,
                                                          market.marketId,
                                                          productItem.lineItemId,
                                                          categoryItem.productId,
                                                        )
                                                      }
                                                    />
                                                  ) : (
                                                    <span>
                                                      {secKey.price !== null
                                                        ? `${formatPrice(truncateToTwoDecimal(secKey.price))}`
                                                        : ""}
                                                    </span>
                                                  )}
                                                </div>
                                              );
                                            }
                                          })
                                        );
                                      })}
                                      {(categoryItem?.laborCodes ?? []).map((productItem, productItemIndex) => {
                                        if (
                                          tablePricesData[market.marketId]?.length == 0 ||
                                          !tablePricesData[market.marketId]?.find(
                                            (x) =>
                                              x.productId == productItem.productId &&
                                              x.lineItemId == productItem.lineItemId,
                                          )
                                        ) {
                                          const globalTDIndex = itemCounter++;
                                          const uniqueDivIndex = `${market.marketId}${index}${globalTRIndex}${globalTDIndex}`;
                                          uniqueIndexSet.add(uniqueDivIndex);
                                          return (
                                            <div
                                              id={uniqueDivIndex}
                                              key={uniqueDivIndex}
                                              className="product-item product-item__prices-align"
                                              style={selectedInputStyle(uniqueDivIndex)}
                                              onClick={() => handleEditClick(uniqueDivIndex)}
                                            >
                                              {isEditing.get(uniqueDivIndex) ? (
                                                <input
                                                  type="text"
                                                  ref={editInputRef}
                                                  className="product-item__prices-input"
                                                  onKeyDown={(event) =>
                                                    handleNavigation(
                                                      marketIndex,
                                                      uniqueDivIndex,
                                                      event,
                                                      market.marketId,
                                                      productItem.lineItemId,
                                                      categoryItem.productId,
                                                    )
                                                  }
                                                  onBlur={(event) =>
                                                    handleBlur(
                                                      event,
                                                      market.marketId,
                                                      productItem.lineItemId,
                                                      categoryItem.productId,
                                                    )
                                                  }
                                                />
                                              ) : (
                                                <span></span>
                                              )}
                                            </div>
                                          );
                                        }

                                        return (
                                          tablePricesData[market.marketId]?.length !== 0 &&
                                          tablePricesData[market.marketId]?.map((secKey, idx) => {
                                            if (
                                              secKey.productId == productItem.productId &&
                                              secKey.lineItemId == productItem.lineItemId
                                            ) {
                                              const globalTDIndex = itemCounter++;
                                              const uniqueDivIndex =
                                                market.marketId + "" + index + "" + globalTRIndex + "" + globalTDIndex;
                                              uniqueIndexSet.add(uniqueDivIndex);
                                              return (
                                                <div
                                                  id={uniqueDivIndex}
                                                  key={uniqueDivIndex}
                                                  className="product-item product-item__prices-align"
                                                  style={selectedInputStyle(uniqueDivIndex)}
                                                  onClick={() => handleEditClick(uniqueDivIndex)}
                                                >
                                                  {isEditing.get(uniqueDivIndex) ? (
                                                    <input
                                                      type="text"
                                                      ref={editInputRef}
                                                      defaultValue={
                                                        secKey.price === null || secKey.price === ""
                                                          ? ""
                                                          : truncateToTwoDecimal(secKey.price)
                                                      }
                                                      className="product-item__prices-input"
                                                      onKeyDown={(event) =>
                                                        handleNavigation(
                                                          marketIndex,
                                                          uniqueDivIndex,
                                                          event,
                                                          market.marketId,
                                                          productItem.lineItemId,
                                                          categoryItem.productId,
                                                        )
                                                      }
                                                      onBlur={(event) =>
                                                        handleBlur(
                                                          event,
                                                          market.marketId,
                                                          productItem.lineItemId,
                                                          categoryItem.productId,
                                                        )
                                                      }
                                                    />
                                                  ) : (
                                                    <span>
                                                      {secKey.price !== null
                                                        ? `${formatPrice(truncateToTwoDecimal(secKey.price))}`
                                                        : ""}
                                                    </span>
                                                  )}
                                                </div>
                                              );
                                            }
                                          })
                                        );
                                      })}
                                      {totalPriceForProduct &&
                                      Object.keys(totalPriceForProduct)?.some((x) => x == categoryItem.productId) ? (
                                        Object.keys(totalPriceForProduct)?.map((productId) => {
                                          if (productId == categoryItem.productId && categoryItem?.typeId != 3) {
                                            return (
                                              <div
                                                key={categoryIndex}
                                                className="product-item product-item__prices-align totalVal"
                                              >
                                                {`$${formatPrice(
                                                  truncateToTwoDecimal(
                                                    totalPriceForProduct[productId][market.marketId] || 0,
                                                  ),
                                                )}`}
                                              </div>
                                            );
                                          }
                                        })
                                      ) : categoryItem?.typeId != 3 ? (
                                        <div className="product-item product-item__prices-align totalVal">$0.00</div>
                                      ) : null}
                                      {categoryItem?.typeId == 1 &&
                                        categoryItem?.categoryId == 1 &&
                                        categoryItem?.products &&
                                        categoryItem?.products?.map((productItem, productItemIndex) => {
                                          if (
                                            productItem.name?.substring(0, 3) === "Pad" &&
                                            (tablePricesData[market.marketId]?.length == 0 ||
                                              !tablePricesData[market.marketId]?.find(
                                                (x) =>
                                                  x.productId == productItem.productId &&
                                                  x.lineItemId == productItem.lineItemId,
                                              ))
                                          ) {
                                            const globalTDIndex = itemCounter++;
                                            const uniqueDivIndex =
                                              market.marketId + "" + index + "" + globalTRIndex + "" + globalTDIndex;
                                            uniqueIndexSet.add(uniqueDivIndex);
                                            return (
                                              <>
                                                <div
                                                  id={uniqueDivIndex}
                                                  key={uniqueDivIndex}
                                                  className="product-item product-item__prices-align"
                                                  style={selectedInputStyle(uniqueDivIndex)}
                                                  onClick={() => handleEditClick(uniqueDivIndex)}
                                                >
                                                  {isEditing.get(uniqueDivIndex) ? (
                                                    <input
                                                      type="text"
                                                      ref={editInputRef}
                                                      className="product-item__prices-input"
                                                      onKeyDown={(event) =>
                                                        handleNavigation(
                                                          marketIndex,
                                                          uniqueDivIndex,
                                                          event,
                                                          market.marketId,
                                                          productItem.lineItemId,
                                                          categoryItem.productId,
                                                          true,
                                                        )
                                                      }
                                                      onBlur={(event) =>
                                                        handleBlur(
                                                          event,
                                                          market.marketId,
                                                          productItem.lineItemId,
                                                          categoryItem.productId,
                                                          true,
                                                        )
                                                      }
                                                    />
                                                  ) : (
                                                    <span></span>
                                                  )}
                                                </div>
                                                <div className="product-item product-item__prices-align totalVal">
                                                  {`$${formatPrice(
                                                    truncateToTwoDecimal(
                                                      totalPriceForProduct[productItem.productId]?.[market.marketId] ||
                                                        0,
                                                    ),
                                                  )}`}
                                                </div>
                                              </>
                                            );
                                          }

                                          return (
                                            productItem.name?.substring(0, 3) === "Pad" &&
                                            tablePricesData[market.marketId]?.length !== 0 &&
                                            tablePricesData[market.marketId]?.map((secKey, idx) => {
                                              if (
                                                secKey.productId == productItem.productId &&
                                                secKey.lineItemId == productItem.lineItemId
                                              ) {
                                                const globalTDIndex = itemCounter++;
                                                const uniqueDivIndex =
                                                  market.marketId +
                                                  "" +
                                                  index +
                                                  "" +
                                                  globalTRIndex +
                                                  "" +
                                                  globalTDIndex;
                                                uniqueIndexSet.add(uniqueDivIndex);
                                                return (
                                                  <>
                                                    <div
                                                      id={uniqueDivIndex}
                                                      key={uniqueDivIndex}
                                                      className="product-item product-item__prices-align"
                                                      style={selectedInputStyle(uniqueDivIndex)}
                                                      onClick={() => handleEditClick(uniqueDivIndex)}
                                                    >
                                                      {isEditing.get(uniqueDivIndex) ? (
                                                        <input
                                                          type="text"
                                                          ref={editInputRef}
                                                          defaultValue={
                                                            secKey.price === null || secKey.price === ""
                                                              ? ""
                                                              : truncateToTwoDecimal(secKey.price)
                                                          }
                                                          className="product-item__prices-input"
                                                          onKeyDown={(event) =>
                                                            handleNavigation(
                                                              marketIndex,
                                                              uniqueDivIndex,
                                                              event,
                                                              market.marketId,
                                                              productItem.lineItemId,
                                                              categoryItem.productId,
                                                              true,
                                                            )
                                                          }
                                                          onBlur={(event) =>
                                                            handleBlur(
                                                              event,
                                                              market.marketId,
                                                              productItem.lineItemId,
                                                              categoryItem.productId,
                                                              true,
                                                            )
                                                          }
                                                        />
                                                      ) : (
                                                        <span>
                                                          {secKey.price !== null
                                                            ? `${formatPrice(truncateToTwoDecimal(secKey.price))}`
                                                            : ""}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div className="product-item product-item__prices-align totalVal">
                                                      $
                                                      {(() => {
                                                        const subtotalInCents = Math.round(
                                                          totalPriceForProduct?.[secKey.productId]?.[market.marketId] *
                                                            100,
                                                        );

                                                        const padTotalInCents = secKey.price * 100;
                                                        const subtotalWithPad =
                                                          (subtotalInCents + padTotalInCents) / 100;
                                                        return formatPrice(truncateToTwoDecimal(subtotalWithPad));
                                                      })()}
                                                    </div>
                                                  </>
                                                );
                                              }
                                            })
                                          );
                                        })}
                                    </td>
                                  );
                                })
                              : null}
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isShowAddCategoryModal.isShow && isShowAddCategoryModal.typeId !== 3 && (
        <Modal
          title="Add Category"
          handleSave={() => confirmAddCategory(isShowAddCategoryModal.typeId)}
          handleCancel={handleCancelModal}
          size={"lg"}
          sectionName={""}
          closeButtonText="Cancel"
          saveButtonText="Add"
          isSaveBtnDisabled={!isCategorySelected}
        >
          <div className="container-fluid p-2 border-0 m-0 add-item-modal">
            <div className="search-form-container__fields">
              <form className="search-form-container__fields__form">
                <div className="row align-items-center">
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group my-0">
                    <SelectInput
                      inputRef={categoryRef}
                      id="progCategory"
                      options={categoriesList || []}
                      label={"Select a Category"}
                      defaultValue={"Select a Value"}
                      value={categoryData?.categoryName || ""}
                      handleSelectInput={(e, value) => onCategoryChange(e, value)}
                    />
                    <span className="error-text my-3">{categoryError}</span>
                  </div>
                  {categoryData?.categoryName && (
                    <>
                      <div className="category-modal__title">
                        <span className="category-modal__title--text">Products</span>
                      </div>
                      {productsList.map((product, index) => {
                        return (
                          <>
                            {product.name === "Product Name" ? (
                              <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group my-0">
                                <InputField
                                  type="text"
                                  id="productName"
                                  label="Product Name"
                                  placeholder={"Enter Product Name"}
                                  fieldClasses="form-control search-form-container__fields__form-group__item"
                                  handleOnBlur={(e) => onLineItemsChange(e)}
                                  disabled={false}
                                  isRequired={true}
                                />
                                <span className="error-text my-3">{productNameError}</span>
                              </div>
                            ) : (
                              <>
                                <div className="category-modal__title">
                                  <span className="category-modal__title--text">Additional Product</span>
                                </div>
                                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                                  <InputField
                                    type="text"
                                    id="productName"
                                    label="Product Name"
                                    placeholder={"Enter Product Name"}
                                    fieldClasses="form-control search-form-container__fields__form-group__item"
                                    value={lineItemsData.padName || "Pad"}
                                    disabled={true}
                                    isRequired={true}
                                  />
                                </div>
                                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                                  <InputField
                                    type="text"
                                    id="padDescripton"
                                    label="Description"
                                    placeholder={"Enter Description"}
                                    fieldClasses="form-control search-form-container__fields__form-group__item"
                                    handleOnBlur={(e) => onLineItemsChange(e)}
                                    disabled={false}
                                    isRequired={false}
                                  />
                                </div>
                              </>
                            )}
                          </>
                        );
                      })}
                      <div className="category-modal__title">
                        <span className="category-modal__title--text">Labor Codes</span>
                      </div>
                      {laborCodesList.map((labor, index) => {
                        return (
                          <div className="labor-section">{`${labor.laborCode} - ${labor.laborCodeDescription}`}</div>
                        );
                      })}
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </Modal>
      )}

      {isShowAddCategoryModal.isShow && isShowAddCategoryModal.typeId === 3 && (
        <Modal
          title={"Add Category"}
          handleSave={() => confirmAddCategory(isShowAddCategoryModal.typeId)}
          handleCancel={handleCancelModal}
          size={"lg"}
          sectionName={""}
          closeButtonText={"Cancel"}
          saveButtonText={"Add"}
          isSaveBtnDisabled={!isCategorySelected}
        >
          <>
            <div className="container-fluid p-2 border-0 m-0">
              <div className="search-form-container__fields">
                <form className="search-form-container__fields__form">
                  <div className="row align-items-center">
                    <div className="category-modal__title d-flex justify-content-between">
                      <div className="d-flex category-modal__title--text">Products</div>
                      <div
                        className="d-flex category-modal__title--edit-text cursor-style"
                        onClick={() => isShowAlaCarteInputs("product", true)}
                      >
                        +Add Products
                      </div>
                    </div>

                    {alaCarteItems.isShowProductInput && (
                      <>
                        <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group">
                          <InputField
                            type="text"
                            id="productName"
                            label="Product Name"
                            placeholder={"Enter Product Name"}
                            fieldClasses="form-control search-form-container__fields__form-group__item"
                            handleOnBlur={(e) => onAlaCarteFieldsChange(e)}
                            disabled={false}
                            isRequired={true}
                          />
                          <span className="error-text my-3" style={{ height: "36px" }}>
                            {productNameError}
                          </span>
                        </div>
                        <div className="col-12 col-sm-12 col-md-3 col-lg-3 search-form-container__fields__form-group">
                          <InputField
                            type="text"
                            id="unitOfMeasurement"
                            label="Unit of Measurement"
                            placeholder={"Unit of measurement"}
                            fieldClasses="form-control search-form-container__fields__form-group__item"
                            handleOnBlur={(e) => onAlaCarteFieldsChange(e)}
                            disabled={false}
                            isRequired={true}
                          />
                          <span className="error-text my-3" style={{ height: "36px" }}>
                            {unitErrorMsg}
                          </span>
                        </div>
                        <div className="col-12 col-sm-12 col-md-2 col-lg-2" style={{ marginTop: "-55px" }}>
                          <Button className="w-100" onClick={() => addAlaCarteItems("product")}>
                            Add
                          </Button>
                        </div>
                        <div className="col-12 col-sm-12 col-md-2 col-lg-2" style={{ marginTop: "-55px" }}>
                          <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={() => isShowAlaCarteInputs("product", false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}

                    {alaCarteItems?.products?.map((product, index) => {
                      return <div className="labor-section">{`${product.name} (${product.unitOfMeasurement})`}</div>;
                    })}

                    <div className="category-modal__title d-flex justify-content-between ">
                      <div className="d-flex category-modal__title--text">Labor Codes</div>
                      <div
                        className="d-flex category-modal__title--edit-text cursor-style"
                        onClick={() => isShowAlaCarteInputs("labor", true)}
                      >
                        +Add Labor Codes
                      </div>
                    </div>
                    {alaCarteItems.isShowLaborInput && (
                      <div className="d-flex flex-column px-4 mx-3 py-4 px-md-0 mx-md-0 labor-codes-fields-container">
                        <div className="search-form-container__fields">
                          <form className="search-form-container__fields__form">
                            <div className="row align-items-center">
                              <div className="col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group m-md-0">
                                <MultiSelectInput
                                  inputRef={laborCodesNode}
                                  options={
                                    laborCodeOptions
                                      ? laborCodeOptions.filter(
                                          (laborCodeItem) =>
                                            !(
                                              selectedLaborCodes.includes(laborCodeItem.laborCode) ||
                                              existingLaborCode.includes(laborCodeItem.laborCode)
                                            ),
                                        )
                                      : []
                                  }
                                  defaultValue="Select any labor code"
                                  label="Labor Code"
                                  handleSelectInput={handleLaborCodeLookupValue}
                                  selectedValues={
                                    alaCarteData.laborCode
                                      ? [alaCarteData.laborCode + " - " + alaCarteData.laborCodeDescription]
                                      : []
                                  }
                                  isLookup={true}
                                  isCloseAfterSelection={true}
                                  isRequired={true}
                                />
                                <span className="error-text my-3" style={{ height: "36px" }}>
                                  {productNameError}
                                </span>
                              </div>
                              <div className="col-12 col-sm-12 col-md-3 col-lg-3 search-form-container__fields__form-group m-md-0">
                                <InputField
                                  type="text"
                                  id="unitOfMeasurement"
                                  label="Unit of Measurement"
                                  placeholder={"Unit of measurement"}
                                  fieldClasses="form-control search-form-container__fields__form-group__item"
                                  handleOnBlur={(e) => onAlaCarteFieldsChange(e)}
                                  disabled={false}
                                  isRequired={true}
                                />
                                <span className="error-text my-3" style={{ height: "36px" }}>
                                  {unitErrorMsg}
                                </span>
                              </div>
                              <div className="col-12 col-sm-12 col-md-2 col-lg-2" style={{ marginTop: "-24px" }}>
                                <Button className="w-100" onClick={() => addAlaCarteItems("labor")}>
                                  Add
                                </Button>
                              </div>
                              <div className="col-12 col-sm-12 col-md-2 col-lg-2" style={{ marginTop: "-24px" }}>
                                <Button
                                  variant="outline-primary"
                                  className="w-100"
                                  onClick={() => isShowAlaCarteInputs("labor", false)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}

                    {alaCarteItems?.laborCodes?.map((labor, index) => {
                      return (
                        <div className="labor-section">{`${labor.laborCode} - ${labor.laborCodeDescription} (${labor.unitOfMeasurement})`}</div>
                      );
                    })}
                  </div>
                </form>
              </div>
            </div>
          </>
        </Modal>
      )}

      {isShowAddMarketModal && (
        <Modal
          title={isEditMarket ? "Edit Market" : "Add Market"}
          handleSave={confirmAddMarket}
          handleCancel={handleCancelModal}
          handleDelete={handleDeleteMarket}
          size="lg"
          sectionName={""}
          closeButtonText={"Cancel"}
          deleteButtonText={isEditMarket ? "Delete" : ""}
          saveButtonText={isEditMarket ? "Save" : "Add"}
        >
          <div className="container-fluid p-2 border-0 m-0 add-market-modal">
            <div className="search-form-container__fields">
              <form className="search-form-container__fields__form">
                <div className="row align-items-center">
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                    <MultiSelectInput
                      inputRef={storeNumRef}
                      options={storeNumberList ?? []}
                      defaultValue={"Select a Value"}
                      label={"Select a Store"}
                      handleSelectInput={handleStoreValues}
                      selectedValues={marketData.storeNumber ? [marketData.storeNumber] : []}
                      isCloseAfterSelection={true}
                      isProgStoreOptions={true}
                    />
                    <span className="error-text">{marketDataErrors.storeNumber}</span>
                  </div>
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                    <InputField
                      refVal={marketInputRef}
                      type="text"
                      id="marketName"
                      label="Market Name"
                      placeholder={"Enter Market Name"}
                      fieldClasses="form-control search-form-container__fields__form-group__item"
                      defaultValue={marketData.marketName}
                      handleOnBlur={(e) => {
                        const { id, value } = e.target;
                        setMarketDataErrors({
                          ...marketDataErrors,
                          marketName: value ? "" : "Please enter Market Name",
                        });
                        setMarketData({ ...marketData, [id]: value });
                        setIsMarketDataChanged(true);
                      }}
                      disabled={false}
                      isRequired={false}
                    />
                    <span className="error-text">{marketDataErrors.marketName}</span>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal>
      )}
      {showEditProgramModal && <ModalContents setIsModalOpen={setShowEditProgramModal} />}
      {showDeleteLineModal && <DeleteLineModal itemToDelete={itemToDelete} setIsModalOpen={setShowDeleteLineModal} />}
    </>
  );
};

export default ProgramDataLoader;
